import React, { useState, useEffect } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { Link } from "react-router-dom";
import Button from "./Button";
import { preventOrphan, formatProductTitle, splitMobileTitle, formatSlogan } from '../utils/text/textFormatting';
import { toUrlFriendly } from "../utils/urlHelpers";

const shimmerStyle = {
  animation: "shimmer 2s infinite linear",
  background: "linear-gradient(to right, #e0e0e0 8%, #f0f0f0 18%, #e0e0e0 33%)",
  backgroundSize: "1000px 100%",
};

function SkeletonBlock({ className, style }) {
  return (
    <div
      className={`${className} relative overflow-hidden bg-[#A9AAAC] rounded-md`}
      style={{ ...shimmerStyle, ...style }}
    />
  );
}

const MobileProductCard = ({ product, sequence = 0, isProfessional = false }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isTextLoaded, setIsTextLoaded] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [localProduct, setLocalProduct] = useState(null);

  const cardRef = React.useRef(null);
  const isInView = useInView(cardRef, { once: true, margin: "-100px" });

  const hasProduct = product && product.fields;

  useEffect(() => {
    if (hasProduct) {
      setLocalProduct(product);
    }
  }, [hasProduct, product]);

  useEffect(() => {
    if (localProduct && localProduct.fields) {
      setIsTextLoaded(true);
    }
  }, [localProduct]);

  useEffect(() => {
    if (isFlipped && !isHovering) {
      const timer = setTimeout(() => {
        setIsFlipped(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isFlipped, isHovering]);

  const { sys } = localProduct || {};
  const {
    productTitle,
    slogan,
    productHeroImage,
    price,
    buyNowButtonUrl,
    detailedProductDescription,
  } = (localProduct && localProduct.fields) || {};

  const imageUrl = productHeroImage?.fields?.file?.url || null;
  
  const handleCardClick = (e) => {
    if (
      e.target.tagName === "BUTTON" || 
      e.target.closest("button") ||
      e.target.tagName === "A" || 
      e.target.closest("a") ||
      isProfessional
    ) {
      return;
    }
    setIsFlipped((prev) => !prev);
  };

  // Animation variants for sequential animations
  const imageVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.1 + (sequence * 0.1),
        duration: 0.2,
        ease: "easeOut"
      }
    }
  };

  const textVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.1 + (sequence * 0.1) + (i * 0.03),
        duration: 0.2,
        ease: "easeOut"
      }
    })
  };

  const cleanText = (text) => {
    if (!text) return '';
    return text
      .replace(/\s+/g, ' ')  // Replace multiple spaces with single space
      .replace(/\s+\./g, '.') // Remove spaces before periods
      .replace(/\.\s+/g, '. ') // Ensure single space after periods
      .replace(/\s+,/g, ',') // Remove spaces before commas
      .replace(/,\s+/g, ', ') // Ensure single space after commas
      .trim(); // Remove leading/trailing spaces
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return '';
    
    // Clean the text first
    const cleanedText = cleanText(text);
    const words = cleanedText.split(' ');
    
    // For titles with 3 or more words
    if (words.length >= 3) {
      // For 3 words, show last word
      if (words.length === 3) {
        const lastWord = words[2];
        const remainingText = words.slice(0, 2).join(' ');
        
        if (remainingText.length + lastWord.length + 3 <= maxLength) {
          return cleanedText;
        }
        
        const availableSpace = maxLength - lastWord.length - 3;
        const truncatedRemaining = remainingText.slice(0, availableSpace);
        return `${truncatedRemaining}... ${lastWord}`;
      }
      
      // For 4 or more words, show last two words
      const lastTwoWords = words.slice(-2).join(' ');
      const remainingText = words.slice(0, -2).join(' ');
      
      if (remainingText.length + lastTwoWords.length + 3 <= maxLength) {
        return cleanedText;
      }
      
      const availableSpace = maxLength - lastTwoWords.length - 3;
      const truncatedRemaining = remainingText.slice(0, availableSpace);
      return `${truncatedRemaining}... ${lastTwoWords}`;
    }
    
    // For 1-2 words, use regular truncation
    if (cleanedText.length <= maxLength) return cleanedText;
    return cleanedText.slice(0, maxLength) + '...';
  };

  if (!hasProduct) {
    return (
      <div className="relative w-full h-[220px] md:h-[250px]">
        <div className="w-full h-full bg-white rounded-3xl shadow-md p-1">
          <div className="flex flex-row items-center justify-center">
            <SkeletonBlock className="w-1/3 h-full mr-1" />
            <div className="flex flex-col w-2/3">
              <SkeletonBlock className="w-48 h-6 mb-1" />
              <SkeletonBlock className="w-56 h-4 mb-1" />
              <SkeletonBlock className="w-24 h-5 mb-1" />
              <SkeletonBlock className="w-32 h-6" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={cardRef}
      className="relative w-full h-[200px] md:h-[220px]"
      style={{
        perspective: "1000px",
        maxWidth: "100%",
        margin: "0 auto",
        padding: "0.125rem"
      }}
      onClick={handleCardClick}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <motion.div
        className="w-full h-full relative"
        style={{ transformStyle: "preserve-3d" }}
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        initial={{ rotateY: 0 }}
        transition={{ duration: 0.4, ease: "easeInOut", delay: sequence * 0.1 }}
      >
        {/* FRONT SIDE */}
        <div
          className="absolute w-full h-full bg-white rounded-2xl shadow-md p-1"
          style={{ backfaceVisibility: "hidden" }}
        >
          <AnimatePresence>
            {(!isImageLoaded || !isTextLoaded) && (
              <motion.div
                key="skeleton"
                className="absolute inset-0 flex flex-row items-center justify-center bg-white rounded-2xl"
                initial={{ opacity: 1 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, transition: { duration: 0.5 } }}
              >
                {!isImageLoaded && (
                  <SkeletonBlock className="w-[42%] h-full mr-1" />
                )}
                {!isTextLoaded && (
                  <div className="flex flex-col w-[58%]">
                    <SkeletonBlock className="w-full h-8 mb-1" />
                    <SkeletonBlock className="w-full h-4 mb-1" />
                    <SkeletonBlock className="w-24 h-5 mb-1" />
                    <SkeletonBlock className="w-32 h-6" />
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: isImageLoaded && isTextLoaded ? 1 : 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-row items-center justify-between h-full w-full"
          >
            {/* Image Container */}
            <div className="w-[42%] h-full flex items-center justify-center">
              {imageUrl && (
                <motion.img
                  custom={0}
                  initial="hidden"
                  animate={isInView ? "visible" : "hidden"}
                  variants={imageVariants}
                  src={imageUrl}
                  alt={productTitle || "Product Image"}
                  className="w-full h-auto max-h-full object-contain"
                  onLoad={() => setIsImageLoaded(true)}
                />
              )}
            </div>

            {/* Content Container */}
            <div className="flex flex-col justify-between h-full w-[58%] pr-1">
              {/* Product Title - Mobile */}
              {isTextLoaded && productTitle && (
                <motion.h3
                  custom={0}
                  initial="hidden"
                  animate={isInView ? "visible" : "hidden"}
                  variants={textVariants}
                  className="font-bold text-gray-900 tracking-wide text-left w-full"
                  style={{
                    fontFamily: "Geogrotesque, sans-serif",
                    textTransform: "none",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                    textAlign: "left",
                    width: "100%",
                    fontSize: "clamp(1.25rem, 1.8vw, 1.5rem)",
                    marginBottom: "0.125rem",
                    lineHeight: "1.2",
                    height: "2.4em"
                  }}
                >
                  {(() => {
                    const raw = cleanText(formatProductTitle(productTitle));
                    const [line1, line2] = splitMobileTitle(raw);
                    return (
                      <>
                        <span>{line1}</span>
                        {line2 && <span>{line2}</span>}
                      </>
                    );
                  })()}
                </motion.h3>
              )}

              {/* Slogan */}
              {isTextLoaded && slogan && (
                <motion.p
                  custom={1}
                  initial="hidden"
                  animate={isInView ? "visible" : "hidden"}
                  variants={textVariants}
                  className="text-gray-600 text-left w-full"
                  style={{ 
                    fontFamily: "Open Sans, sans-serif",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                    lineHeight: "1.2",
                    textAlign: "left",
                    width: "100%",
                    fontSize: "clamp(0.875rem, 1.3vw, 1rem)",
                    marginBottom: "0.125rem",
                    height: isProfessional ? "2.4em" : "2.4em",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical"
                  }}
                >
                  {formatSlogan(slogan).split('\n').map((line, index) => (
                    <span key={index}>{line}</span>
                  ))}
                </motion.p>
              )}

              {/* Button Container */}
              <motion.div
                custom={3}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                variants={textVariants}
                className="w-full mt-auto flex flex-col gap-1"
                style={{ 
                  height: isProfessional ? "2rem" : "4rem",
                  justifyContent: "flex-end",
                  paddingBottom: "0.25rem"
                }}
              >
                {/* Price (only for household) - Moved above buttons */}
                {isTextLoaded && typeof price === "number" && !isProfessional && (
                  <motion.p
                    custom={2}
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                    variants={textVariants}
                    className="font-bold text-gray-900 text-left w-full mb-1"
                    style={{ 
                      fontFamily: "Geogrotesque, sans-serif",
                      textAlign: "left",
                      width: "100%",
                      fontSize: "clamp(1rem, 1.4vw, 1.2rem)",
                      height: "1.2em"
                    }}
                  >
                    ${price.toFixed(2)}
                  </motion.p>
                )}

                {!isProfessional && isTextLoaded && buyNowButtonUrl && (
                  <Button
                    to={buyNowButtonUrl === "Coming Soon!" ? "Coming Soon!" : undefined}
                    href={buyNowButtonUrl !== "Coming Soon!" ? buyNowButtonUrl : undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="primary"
                    size="small"
                    fullWidth
                    onClick={(e) => {
                      e.stopPropagation();
                      if (buyNowButtonUrl !== "Coming Soon!") {
                        window.open(buyNowButtonUrl, '_blank');
                      }
                    }}
                    style={{ 
                      padding: "0.25rem 0.5rem",
                      fontSize: "clamp(0.75rem, 1.2vw, 0.875rem)",
                      height: "1.75rem"
                    }}
                  >
                    {buyNowButtonUrl === "Coming Soon!" ? "Coming Soon!" : "Buy Now"}
                  </Button>
                )}
                {isTextLoaded && sys?.id && (
                  <Link
                    to={`/${isProfessional ? 'industrial' : 'household'}/product/${isProfessional ? 'industrial' : 'household'}/${toUrlFriendly(productTitle)}`}
                    className="w-full"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <Button
                      variant={isProfessional ? "primary" : "secondary"}
                      size="small"
                      fullWidth
                      style={{ 
                        padding: "0.25rem 0.5rem",
                        fontSize: "clamp(0.75rem, 1.2vw, 0.875rem)",
                        height: "1.75rem"
                      }}
                    >
                      More Details
                    </Button>
                  </Link>
                )}
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* BACK SIDE */}
        <div
          className="absolute w-full h-full bg-white rounded-2xl shadow-md p-2 flex flex-col justify-center items-center text-center"
          style={{
            transform: "rotateY(180deg)",
            backfaceVisibility: "hidden",
          }}
        >
          {detailedProductDescription ? (
            <>
              <h3
                className="text-base font-semibold text-gray-900 mb-1"
                style={{ fontFamily: "Geogrotesque, sans-serif" }}
              >
                Product Details
              </h3>
              <p
                className="text-gray-700 leading-relaxed text-xs overflow-auto px-1 line-clamp-6"
                style={{ fontFamily: "Geogrotesque, sans-serif" }}
              >
                {preventOrphan(detailedProductDescription)}
              </p>
            </>
          ) : (
            <div className="flex-grow flex justify-center items-center text-gray-500 text-xs">
              No detailed description available.
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default MobileProductCard; 